import React, { useState, useRef, useEffect, useCallback } from "react";
import { safeFormat } from '@/lib/safeDate';
import { useTranslation } from "react-i18next";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Textarea } from "@/components/ui/textarea";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Phone, Clock, Loader2, PhoneIncoming, PhoneOutgoing, Play, Pause, RefreshCw, PhoneCall, StickyNote, Edit2, Save, X, ChevronDown, ChevronUp, Calendar, Video, XCircle } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { format } from "date-fns";
import { formatDateTimeEN } from "@/lib/dateUtils";
import { useCalls } from "@/hooks/useCalls";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { ScheduleCallModal } from "@/components/calls/ScheduleCallModal";
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useScheduledCalls } from "@/hooks/useScheduledCalls";
import { ScheduledCallCard } from "@/components/calls/ScheduledCallCard";
import { QuickVideoCallButton } from "@/components/calls/QuickVideoCallButton";
import { ReferralCallCompletionModal } from "@/components/calls/ReferralCallCompletionModal";
import { VideoRingDetailsPanel } from "@/components/calls/VideoRingDetailsPanel";
import { RescheduleCallModal } from "@/components/calls/RescheduleCallModal";

export default function CallsTab({ contactId, leadId, dealId, onCallBack }) {
  const { t } = useTranslation();
  const { calls, loading: isLoading, loadCalls, addCallNote, scheduleCall, markCallComplete, cancelScheduledCall, deleteScheduledCall, rescheduleCall } = useCalls(contactId, leadId, dealId);
  const { scheduledCalls, loading: scheduledLoading, loadScheduledCalls } = useScheduledCalls({ 
    contactId, 
    leadId,
    dealId,
    // Show all scheduled, completed, and canceled meetings - not just scheduled
    // This ensures completed meetings remain visible with status badges
  });
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [activeTab, setActiveTab] = useState("history");
  const [playingRecordingId, setPlayingRecordingId] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [contactsMap, setContactsMap] = useState({});
  const [usersMap, setUsersMap] = useState({});
  const [usersByIdMap, setUsersByIdMap] = useState({});
  const [usersByAuthIdMap, setUsersByAuthIdMap] = useState({});
  const [handlerPhoneMap, setHandlerPhoneMap] = useState({});
  const [directionFilter, setDirectionFilter] = useState('all');
  const [userFilter, setUserFilter] = useState('all');
  const [channelFilter, setChannelFilter] = useState('all');
  const [availableUsers, setAvailableUsers] = useState([]);
  const [addingNoteCallId, setAddingNoteCallId] = useState(null);
  const [newNoteText, setNewNoteText] = useState('');
  const [expandedCalls, setExpandedCalls] = useState(new Set());
  const [editingCall, setEditingCall] = useState(null);
  const [referralModalOpen, setReferralModalOpen] = useState(false);
  const [selectedCallForReferral, setSelectedCallForReferral] = useState(null);
  const [videoCallRequesters, setVideoCallRequesters] = useState({}); // callId -> { name, initials }
  const [rescheduleModalOpen, setRescheduleModalOpen] = useState(false);
  const [callToReschedule, setCallToReschedule] = useState(null);
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const [callToCancel, setCallToCancel] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const audioRef = useRef(null);

  // Callbacks memoizados - DEVEM estar antes de qualquer early return
  const handleScheduleCall = useCallback(async (callData) => {
    await scheduleCall(callData);
  }, [scheduleCall]);

  const handleOpenCancelConfirm = useCallback((call) => {
    setCallToCancel(call);
    setCancelReason('');
    setCancelConfirmOpen(true);
  }, []);

  const handleConfirmCancel = useCallback(async () => {
    if (!callToCancel) return;
    
    try {
      await cancelScheduledCall(callToCancel.id, cancelReason || "Cancelada pelo usuário");
      toast.success(t('calls.callCanceled', 'Reunião cancelada'));
    } catch (error) {
      console.error('Error canceling call:', error);
      toast.error(t('calls.errorCancelingCall', 'Erro ao cancelar reunião'));
    } finally {
      setCancelConfirmOpen(false);
      setCallToCancel(null);
      setCancelReason('');
    }
  }, [callToCancel, cancelReason, cancelScheduledCall, t]);

  const handleCloseScheduleModal = useCallback(() => {
    setShowScheduleModal(false);
    setEditingCall(null);
  }, []);

  const toggleCallExpanded = (callId) => {
    setExpandedCalls(prev => {
      const newSet = new Set(prev);
      if (newSet.has(callId)) {
        newSet.delete(callId);
      } else {
        newSet.add(callId);
      }
      return newSet;
    });
  };

  // Busca informações de contatos e usuários baseado nos números
  useEffect(() => {
    const fetchContactsAndUsers = async () => {
      if (!calls || calls.length === 0) return;

      const phoneNumbers = new Set();
      const authUserIds = new Set();
      const handlerUserIds = new Set();
      const videoCallIds = [];
      
      calls.forEach(call => {
        // Adiciona handled_by_app_user_id se existir
        if (call.handled_by_app_user_id) {
          handlerUserIds.add(call.handled_by_app_user_id);
        }
        
        // Collect video call IDs to fetch requesters
        if (call.channel === 'video') {
          videoCallIds.push(call.id);
        }
        
        if (call.from_e164) {
          if (call.from_e164.startsWith('client:')) {
            authUserIds.add(call.from_e164.replace('client:', ''));
          } else {
            phoneNumbers.add(call.from_e164);
          }
        }
        if (call.to_e164) {
          if (call.to_e164.startsWith('client:')) {
            authUserIds.add(call.to_e164.replace('client:', ''));
          } else {
            phoneNumbers.add(call.to_e164);
          }
        }
      });

      // Busca contatos
      const { data: contacts } = await supabase
        .from('contacts')
        .select('id, first_name, last_name, primary_phone')
        .in('primary_phone', Array.from(phoneNumbers));

      // DEPRECATED - leads query removed; contacts only
      
      // Busca usuários app por telefone
      const { data: users } = await supabase
        .from('app_users')
        .select('id, auth_user_id, first_name, last_name, display_name, twilio_phone_number');

      // Busca requesters de video calls
      if (videoCallIds.length > 0) {
        const { data: ringRequests } = await supabase
          .from('video_call_ring_requests')
          .select('call_id, requested_by')
          .in('call_id', videoCallIds);
        
        if (ringRequests && ringRequests.length > 0) {
          const requesterIds = [...new Set(ringRequests.map(r => r.requested_by).filter(Boolean))];
          const { data: requesterUsers } = await supabase
            .from('app_users')
            .select('id, first_name, last_name, display_name')
            .in('id', requesterIds);
          
          const requesterMap = {};
          requesterUsers?.forEach(u => {
            const name = u.display_name || `${u.first_name || ''} ${u.last_name || ''}`.trim() || 'User';
            const initials = name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || 'U';
            requesterMap[u.id] = { name, initials };
          });
          
          const videoRequesters = {};
          ringRequests.forEach(rr => {
            if (rr.requested_by && requesterMap[rr.requested_by]) {
              videoRequesters[rr.call_id] = requesterMap[rr.requested_by];
            }
          });
          setVideoCallRequesters(videoRequesters);
        }
      }

      // Cria mapa de telefone -> contato
      const contactMap = {};
      contacts?.forEach(contact => {
        if (contact.primary_phone) {
          contactMap[contact.primary_phone] = {
            name: `${contact.first_name || ''} ${contact.last_name || ''}`.trim() || 'Contact',
            initials: `${contact.first_name?.[0] || ''}${contact.last_name?.[0] || ''}`.toUpperCase() || 'C'
          };
        }
      });

      // Cria mapa de telefone -> usuário E client:authUserId -> usuário E id -> usuário
      const userMap = {};
      const userIdMap = {};
      const authIdMap = {};
      const handlerPhoneMapTemp = {};
      users?.forEach(user => {
        const name = user.display_name || `${user.first_name || ''} ${user.last_name || ''}`.trim() || 'User';
        const initials = (user.display_name?.[0] || user.first_name?.[0] || 'U').toUpperCase();
        
        const userInfo = { name, initials };
        
        // Mapeia por número de telefone Twilio
        if (user.twilio_phone_number) {
          userMap[user.twilio_phone_number] = userInfo;
          // Mapeia app_user.id -> twilio_phone_number para resolver client: no To
          handlerPhoneMapTemp[user.id] = user.twilio_phone_number;
        }
        
        // Mapeia por client:auth_user_id
        if (user.auth_user_id) {
          userMap[`client:${user.auth_user_id}`] = userInfo;
          authIdMap[user.auth_user_id] = userInfo;
        }
        
        // Mapeia por app_user.id
        userIdMap[user.id] = userInfo;
      });

      setContactsMap(contactMap);
      setUsersMap(userMap);
      setUsersByIdMap(userIdMap);
      setUsersByAuthIdMap(authIdMap);
      setHandlerPhoneMap(handlerPhoneMapTemp);
      
      // Prepara lista de usuários disponíveis para o filtro
      const usersList = users?.map(user => ({
        id: user.id,
        name: user.display_name || `${user.first_name || ''} ${user.last_name || ''}`.trim() || 'User',
      })) || [];
      setAvailableUsers(usersList);
    };

    fetchContactsAndUsers();
  }, [calls]);

  // Realtime subscriptions are managed by useCalls hook - no need to duplicate here

  const getPersonInfo = (phoneNumber) => {
    // Se começa com "client:", é sempre um usuário interno
    if (phoneNumber?.startsWith('client:')) {
      const userInfo = usersMap[phoneNumber];
      if (userInfo) {
        return { ...userInfo, type: 'user' };
      }
    }
    
    // Tenta buscar no mapa de usuários (por telefone Twilio)
    if (usersMap[phoneNumber]) {
      return { ...usersMap[phoneNumber], type: 'user' };
    }
    
    // Tenta buscar no mapa de contatos
    if (contactsMap[phoneNumber]) {
      return { ...contactsMap[phoneNumber], type: 'contact' };
    }

    // Fallback: retorna o número
    return { 
      name: phoneNumber, 
      initials: '?', 
      type: 'unknown' 
    };
  };

  const getToPhone = (call) => {
    if (call.to_e164?.startsWith('client:')) {
      // É uma chamada inbound roteada via Client, resolve para número Twilio real
      return handlerPhoneMap[call.handled_by_app_user_id] || call.to_e164;
    }
    return call.to_e164;
  };


  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  const formatDuration = (seconds) => {
    if (!seconds) return 'N/A';
    const minutes = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  const handleSeek = (value) => {
    if (audioRef.current) {
      const newTime = value[0];
      audioRef.current.currentTime = newTime;
      setCurrentTime(newTime);
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      'completed': { label: t('calls.completed'), className: 'bg-emerald-100 text-emerald-700' },
      'in-progress': { label: t('calls.inProgress'), className: 'bg-blue-100 text-blue-700' },
      'no-answer': { label: t('calls.missed'), className: 'bg-red-100 text-red-700' },
      'busy': { label: t('calls.busy'), className: 'bg-rose-100 text-rose-700' },
      'failed': { label: t('calls.failed'), className: 'bg-rose-100 text-rose-700' },
      'canceled': { label: t('calls.canceled'), className: 'bg-slate-100 text-slate-700' },
      'ringing': { label: t('calls.ringing'), className: 'bg-blue-100 text-blue-700' },
      'initiating': { label: t('calls.initiating'), className: 'bg-slate-100 text-slate-700' },
      'rescheduled': { label: t('calls.rescheduled', 'Remarcada'), className: 'bg-amber-100 text-amber-700' },
    };

    return statusMap[status || ''] || { label: status || t('calls.unknown'), className: 'bg-slate-100 text-slate-700' };
  };

  const handlePlayRecording = async (recording) => {
    // Se já está tocando esta gravação, pausar/retomar
    if (playingRecordingId === recording.id && audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
        setIsPlaying(false);
        toast.info(t('calls.recordingPaused'));
      } else {
        audioRef.current.play();
        setIsPlaying(true);
        toast.success(t('calls.playingRecording'));
      }
      return;
    }

    // Parar áudio anterior se existir
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
      setCurrentTime(0);
      setDuration(0);
    }

    console.log('Tentando reproduzir gravação:', recording);
    
    try {
      console.log('Criando signed URL para:', recording.storage_path);
      const { data, error } = await supabase.storage
        .from('call-recordings')
        .createSignedUrl(recording.storage_path, 3600);
      
      console.log('Resposta do storage:', { data, error });
      
      if (error) {
        console.error('Error fetching recording:', error);
        toast.error(`${t('calls.errorPlayingAudio')}: ${error.message}`);
        return;
      }
      
      if (data?.signedUrl) {
        console.log('URL assinada obtida:', data.signedUrl);
        
        // Testa se o arquivo existe
        try {
          const testResponse = await fetch(data.signedUrl, { method: 'HEAD' });
          console.log('Teste de existência do arquivo:', testResponse.status);
          
          if (!testResponse.ok) {
            throw new Error(`Arquivo não encontrado (${testResponse.status})`);
          }
        } catch (fetchError) {
          console.error('Error verifying file:', fetchError);
          toast.error(t('calls.couldNotPlayRecording'));
          return;
        }
        
        // Cria um elemento de áudio para reproduzir
        const audio = new Audio(data.signedUrl);
        audioRef.current = audio;
        
        audio.addEventListener('loadstart', () => {
          console.log('Starting audio loading...');
          toast.success(t('calls.loadingRecording'));
        });
        
        audio.addEventListener('loadedmetadata', () => {
          console.log('Metadata carregada, duração:', audio.duration);
          setDuration(audio.duration);
        });

        audio.addEventListener('timeupdate', () => {
          setCurrentTime(audio.currentTime);
        });

        audio.addEventListener('canplay', () => {
          console.log('Áudio pronto para reproduzir');
          setPlayingRecordingId(recording.id);
          setIsPlaying(true);
        });
        
        audio.addEventListener('ended', () => {
          console.log('Audio finished');
          setPlayingRecordingId(null);
          setIsPlaying(false);
          setCurrentTime(0);
          toast.success(t('calls.recordingFinished'));
        });
        
        audio.addEventListener('error', (e) => {
          console.error('Audio element error:', e);
          toast.error(t('calls.errorPlayingAudio'));
          setPlayingRecordingId(null);
          setIsPlaying(false);
          setCurrentTime(0);
          setDuration(0);
        });
        
        audio.play().catch(err => {
          console.error('Error playing audio:', err);
          toast.error(t('calls.couldNotPlayRecording'));
          setPlayingRecordingId(null);
          setIsPlaying(false);
        });
      } else {
        toast.error(t('calls.couldNotGetRecordingUrl'));
      }
    } catch (error) {
      console.error('General error playing recording:', error);
      toast.error('Error playing recording. Try again.');
    }
  };

  const handleAddNote = async (callId) => {
    if (!newNoteText.trim()) {
      toast.error(t('calls.pleaseEnterNote'));
      return;
    }

    try {
      await addCallNote(callId, newNoteText.trim());
      toast.success(t('calls.noteAddedSuccessfully'));
      setAddingNoteCallId(null);
      setNewNoteText('');
    } catch (error) {
      console.error('Error adding note:', error);
      toast.error(t('calls.failedToAddNote'));
    }
  };

  const handleCancelAddNote = () => {
    setAddingNoteCallId(null);
    setNewNoteText('');
  };

  const handleEditScheduledCall = (call) => {
    setEditingCall(call);
    setShowScheduleModal(true);
  };

  // Consolida registros de chamadas recebidas duplicados e anexa gravações ao cartão correto
  const areTimesClose = (a, b) => {
    if (!a || !b) return false;
    try {
      return Math.abs(new Date(a).getTime() - new Date(b).getTime()) <= 5 * 60 * 1000; // 5 minutos
    } catch {
      return false;
    }
  };

  // Cria uma cópia mutável da lista e marca duplicados para ocultar
  const hiddenIds = new Set();
  const displayCalls = calls.map((c) => ({ ...c }));

  for (let i = 0; i < displayCalls.length; i++) {
    const call = displayCalls[i];
    if (hiddenIds.has(call.id)) continue;

    // Caso típico: inbound para <Client> (To = client:...), mas gravação foi salva no registro irmão (To = número Twilio)
    if (call.direction === 'inbound' && call.to_e164?.startsWith('client:')) {
      const hasRecs = Array.isArray(call.recordings) && call.recordings.length > 0;
      if (!hasRecs) {
        const sibling = calls.find((s) =>
          s.id !== call.id &&
          s.direction === 'inbound' &&
          s.from_e164 === call.from_e164 &&
          !(s.to_e164?.startsWith('client:')) &&
          areTimesClose(s.started_at || s.created_at, call.started_at || call.created_at)
        );
        if (sibling && sibling.recordings?.length) {
          // Anexa as gravações do irmão a este cartão e oculta o irmão
          call.recordings = sibling.recordings;
          hiddenIds.add(sibling.id);
        }
      }
    }
  }

  // Filtra os registros ocultos (irmãos) para exibição
  let filteredDisplayCalls = displayCalls.filter((c) => !hiddenIds.has(c.id));
  
  // Excluir calls agendados do Call History (só mostrar calls que já aconteceram)
  filteredDisplayCalls = filteredDisplayCalls.filter(call => {
    // Se tem scheduled_for no futuro e status é 'scheduled' ou 'in-progress', NÃO mostrar no history
    if (call.scheduled_for && call.status && ['scheduled', 'in-progress'].includes(call.status)) {
      const scheduledDate = new Date(call.scheduled_for);
      const now = new Date();
      if (scheduledDate > now) {
        return false; // Excluir do history
      }
    }
    return true; // Mostrar no history
  });
  
  // Aplica filtros
  if (directionFilter !== 'all') {
    filteredDisplayCalls = filteredDisplayCalls.filter(call => call.direction === directionFilter);
  }
  
  if (userFilter !== 'all') {
    filteredDisplayCalls = filteredDisplayCalls.filter(call => {
      // Para chamadas outbound, verifica created_by ou handled_by_app_user_id
      if (call.direction === 'outbound') {
        return call.handled_by_app_user_id === userFilter || call.created_by === userFilter;
      }
      // Para chamadas inbound, verifica handled_by_app_user_id
      return call.handled_by_app_user_id === userFilter;
    });
  }

  if (channelFilter !== 'all') {
    filteredDisplayCalls = filteredDisplayCalls.filter(call => call.channel === channelFilter);
  }

  const handleComplete = async (callId) => {
    try {
      await markCallComplete(callId);
      toast.success(t('calls.callMarkedComplete'));
    } catch (error) {
      toast.error(t('calls.errorCompletingCall'));
    }
  };

  const handleCancel = async (callId) => {
    const reason = prompt(t('calls.cancelReason', 'Qual o motivo do cancelamento?'));
    if (reason === null) return; // User clicked Cancel
    
    try {
      await cancelScheduledCall(callId, reason || "Cancelada pelo usuário");
      toast.success(t('calls.callCanceled'));
    } catch (error) {
      toast.error(t('calls.errorCancelingCall'));
    }
  };

  const handleDelete = async (callId) => {
    if (confirm(t('calls.confirmDeleteCall'))) {
      try {
        await deleteScheduledCall(callId);
        toast.success(t('calls.callDeleted'));
      } catch (error) {
        toast.error(t('calls.errorDeletingCall'));
      }
    }
  };

  return (
    <div className="max-w-3xl">
      <div className="flex justify-between items-center mb-4">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1">
          <TabsList>
            <TabsTrigger value="history">{t('calls.callHistory')}</TabsTrigger>
            <TabsTrigger value="scheduled">
              {t('calls.scheduledCalls')}
              {scheduledCalls.length > 0 && (
                <Badge variant="secondary" className="ml-2">{scheduledCalls.length}</Badge>
              )}
            </TabsTrigger>
          </TabsList>
        </Tabs>
        
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              console.log('Reloading calls...');
              loadCalls();
            }}
            className="flex items-center gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            {t('calls.refresh')}
          </Button>
        </div>
      </div>
      
      {/* Filtros */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsContent value="history">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div className="space-y-2">
          <Label htmlFor="direction-filter">{t('calls.direction')}</Label>
          <Select value={directionFilter} onValueChange={setDirectionFilter}>
            <SelectTrigger id="direction-filter">
              <SelectValue placeholder={t('calls.allDirections')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('calls.allDirections')}</SelectItem>
              <SelectItem value="inbound">{t('calls.inbound')}</SelectItem>
              <SelectItem value="outbound">{t('calls.outbound')}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="user-filter">{t('calls.teamMember')}</Label>
          <Select value={userFilter} onValueChange={setUserFilter}>
            <SelectTrigger id="user-filter">
              <SelectValue placeholder={t('calls.allTeamMembers')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('calls.allTeamMembers')}</SelectItem>
              {availableUsers.map(user => (
                <SelectItem key={user.id} value={user.id}>
                  {user.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="channel-filter">{t('calls.callType')}</Label>
          <Select value={channelFilter} onValueChange={setChannelFilter}>
            <SelectTrigger id="channel-filter">
              <SelectValue placeholder={t('calls.allTypes')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('calls.allTypes')}</SelectItem>
              <SelectItem value="voice">
                <div className="flex items-center gap-2">
                  <Phone className="w-3 h-3" />
                  {t('calls.voiceCalls')}
                </div>
              </SelectItem>
              <SelectItem value="video">
                <div className="flex items-center gap-2">
                  <Video className="w-3 h-3" />
                  {t('calls.videoCalls')}
                </div>
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      {filteredDisplayCalls.length > 0 ? (
        <div className="space-y-3">
          {filteredDisplayCalls.map((call) => {
            const statusBadge = getStatusBadge(call.status);
            return (
              <Card key={call.id} className="border-slate-200">
                <Collapsible 
                  open={expandedCalls.has(call.id)}
                  onOpenChange={() => toggleCallExpanded(call.id)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      {call.channel === 'video' ? (
                        <div className="p-2 rounded-lg bg-purple-100">
                          <Video className="w-5 h-5 text-purple-600" />
                        </div>
                      ) : (
                        <div className={`p-2 rounded-lg ${call.direction === 'inbound' ? 'bg-emerald-100' : 'bg-blue-100'}`}>
                          {call.direction === 'inbound' ? (
                            <PhoneIncoming className="w-5 h-5 text-emerald-600" />
                          ) : (
                            <PhoneOutgoing className="w-5 h-5 text-blue-600" />
                          )}
                        </div>
                      )}
                      <div className="flex-1">
                        {/* Header sempre visível - compacto */}
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 space-y-2">
                            {/* Linha 1: Badges + From inline */}
                            <div className="flex items-center gap-2 flex-wrap">
                              {call.channel === 'video' ? (
                                <Badge className={`${
                                  call.scheduled_for 
                                    ? (call.rescheduled_count > 0 
                                        ? 'bg-amber-100 text-amber-700'  // Remarcada = amarelo
                                        : 'bg-green-100 text-green-700') // Agendada = verde
                                    : 'bg-blue-100 text-blue-700'         // Instantânea = azul
                                } border-0 pointer-events-none`}>
                                  {call.scheduled_for 
                                    ? (call.rescheduled_count > 0 
                                        ? t('calls.rescheduled', 'Remarcada')
                                        : t('calls.scheduled', 'Agendada'))
                                    : t('calls.instant', 'Instantânea')}
                                </Badge>
                              ) : (
                                <Badge className={`${call.direction === 'inbound' ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-700'} border-0 pointer-events-none`}>
                                  {call.direction === 'inbound' ? t('calls.inbound') : t('calls.outbound')}
                                </Badge>
                              )}
                              <Badge className={`${statusBadge.className} border-0 pointer-events-none`}>
                                {statusBadge.label}
                              </Badge>
                              {(() => {
                                const maxRecDuration = call.recordings?.reduce((max, r) => 
                                  Math.max(max, r.duration_seconds || 0), 0) || 0;
                                const effectiveDuration = Math.max(call.duration_seconds || 0, maxRecDuration);
                                return effectiveDuration > 0 ? (
                                  <div className="flex items-center gap-1 text-sm text-slate-600">
                                    <Clock className="w-4 h-4" />
                                    <span>{formatDuration(effectiveDuration)}</span>
                                  </div>
                                ) : null;
                              })()}
                              <div className="flex items-center gap-1.5 text-sm text-slate-700">
                                <span className="font-medium">{t('calls.from')}:</span>
                                <Avatar className="w-5 h-5">
                                  <AvatarFallback className="text-xs bg-primary/10 text-primary">
                                    {(() => {
                                      // For video calls, show the requester (who clicked the button)
                                      if (call.channel === 'video' && videoCallRequesters[call.id]) {
                                        return videoCallRequesters[call.id].initials;
                                      }
                                      if (call.handled_by_app_user_id && usersByIdMap[call.handled_by_app_user_id]) {
                                        return usersByIdMap[call.handled_by_app_user_id].initials;
                                      }
                                      if (call.direction === 'outbound' && call.created_by && usersByAuthIdMap[call.created_by]) {
                                        return usersByAuthIdMap[call.created_by].initials;
                                      }
                                      return getPersonInfo(call.from_e164).initials;
                                    })()}
                                  </AvatarFallback>
                                </Avatar>
                                <span className="font-medium">
                                  {(() => {
                                    // For video calls, show the requester (who clicked the button)
                                    if (call.channel === 'video' && videoCallRequesters[call.id]) {
                                      return videoCallRequesters[call.id].name;
                                    }
                                    if (call.handled_by_app_user_id && usersByIdMap[call.handled_by_app_user_id]) {
                                      return usersByIdMap[call.handled_by_app_user_id].name;
                                    }
                                    if (call.direction === 'outbound' && call.created_by && usersByAuthIdMap[call.created_by]) {
                                      return usersByAuthIdMap[call.created_by].name;
                                    }
                                    return getPersonInfo(call.from_e164).name;
                                  })()}
                                </span>
                              </div>
                            </div>
                            {/* Linha 2: Data/hora - para agendadas mostrar scheduled_for */}
                            <p className="text-sm text-slate-600">
                              {call.scheduled_for 
                                ? formatDateTimeEN(call.scheduled_for)
                                : (call.started_at 
                                    ? formatDateTimeEN(call.started_at)
                                    : formatDateTimeEN(call.created_at))}
                              {call.rescheduled_count > 0 && call.original_scheduled_for && (
                                <span className="ml-2 text-xs text-amber-600">
                                  (Original: {formatDateTimeEN(call.original_scheduled_for)})
                                </span>
                              )}
                            </p>
                          </div>
                          {/* Botão de expandir/colapsar */}
                          <CollapsibleTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0 shrink-0">
                              {expandedCalls.has(call.id) ? (
                                <ChevronUp className="h-4 w-4" />
                              ) : (
                                <ChevronDown className="h-4 w-4" />
                              )}
                            </Button>
                          </CollapsibleTrigger>
                        </div>
                        
                        {/* Conteúdo colapsável */}
                        <CollapsibleContent className="space-y-3 mt-3">
                          {/* To: (Who received) */}
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-medium text-slate-500 min-w-[35px]">{t('calls.to')}:</span>
                            <div className="flex items-center gap-2">
                              <Avatar className="w-6 h-6">
                                <AvatarFallback className="text-xs bg-accent/10 text-accent-foreground">
                                  {getPersonInfo(getToPhone(call)).initials}
                                </AvatarFallback>
                              </Avatar>
                              <div className="flex flex-col">
                                <span className="text-sm font-medium text-slate-700">
                                  {getPersonInfo(getToPhone(call)).name}
                                </span>
                                <span className="text-xs text-slate-500">{getToPhone(call)}</span>
                              </div>
                            </div>
                          </div>
                          
                          {call.handler && (
                            <p className="text-xs text-slate-500">
                              {t('calls.handledBy')}: {call.handler.display_name || call.handler.email}
                            </p>
                          )}
                          
                          {/* Criado por - mostra quem agendou a chamada */}
                          {call.scheduled_for && call.created_by && usersByAuthIdMap[call.created_by] && (
                            <div className="flex items-center gap-2 text-xs text-slate-500">
                              <span className="font-medium">{t('calls.createdBy', 'Agendada por')}:</span>
                              <div className="flex items-center gap-1">
                                <Avatar className="w-4 h-4">
                                  <AvatarFallback className="text-[10px] bg-green-100 text-green-700">
                                    {usersByAuthIdMap[call.created_by].initials}
                                  </AvatarFallback>
                                </Avatar>
                                <span>{usersByAuthIdMap[call.created_by].name}</span>
                              </div>
                            </div>
                          )}
                          
                          {/* Call back button */}
                          {onCallBack && call.direction === 'inbound' && call.from_e164 && (
                            <div className="pt-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => onCallBack(call.from_e164)}
                                className="flex items-center gap-2"
                              >
                                <PhoneCall className="w-4 h-4" />
                                {t('calls.callBack')}
                              </Button>
                            </div>
                          )}
                          
                          {/* Notes Section */}
                          <div className="pt-3 border-t border-slate-200">
                            <div className="flex items-center justify-between mb-3">
                              <div className="flex items-center gap-2 text-sm font-medium text-slate-700">
                                <StickyNote className="h-4 w-4" />
                                <span>{t('calls.callNotes')}</span>
                              </div>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setAddingNoteCallId(call.id)}
                              >
                                <Edit2 className="h-3 w-3 mr-1" />
                                {t('calls.addNote')}
                              </Button>
                            </div>

                            {/* Add Note Form */}
                            {addingNoteCallId === call.id && (
                              <div className="space-y-2 mb-4">
                                <Textarea
                                  value={newNoteText}
                                  onChange={(e) => setNewNoteText(e.target.value)}
                                  placeholder={t('calls.writeNote')}
                                  className="min-h-[80px] resize-none text-sm"
                                  maxLength={1000}
                                  autoFocus
                                />
                                <div className="flex items-center justify-between">
                                  <span className="text-xs text-slate-500">
                                    {newNoteText.length}/1000 {t('calls.characters')}
                                  </span>
                                  <div className="flex gap-2">
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={handleCancelAddNote}
                                    >
                                      <X className="h-3 w-3 mr-1" />
                                      {t('common.cancel')}
                                    </Button>
                                    <Button
                                      variant="default"
                                      size="sm"
                                      onClick={() => handleAddNote(call.id)}
                                    >
                                      <Save className="h-3 w-3 mr-1" />
                                      {t('calls.addNote')}
                                    </Button>
                                  </div>
                                </div>
                              </div>
                            )}

                            {/* Existing Notes List */}
                            {(() => {
                              let parsedNotes = [];
                              if (call.notes) {
                                try {
                                  parsedNotes = typeof call.notes === 'string' ? JSON.parse(call.notes) : call.notes;
                                  if (!Array.isArray(parsedNotes)) parsedNotes = [];
                                } catch {
                                  parsedNotes = [];
                                }
                                // Fallback: plain text notes (backwards compat)
                                if (parsedNotes.length === 0 && typeof call.notes === 'string' && call.notes.trim().length > 0) {
                                  parsedNotes = [{ text: call.notes, created_by: 'IA', created_date: null }];
                                }
                              }

                              return parsedNotes.length > 0 ? (
                                <div className="space-y-3">
                                  {parsedNotes.map((note, index) => (
                                    <Card key={index} className={note.created_by === 'IA' || (note.text || '').startsWith('[Resumo IA]') ? "bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-950/20 dark:to-indigo-950/20 border-blue-200 dark:border-blue-800" : "bg-slate-50"}>
                                      <CardContent className="p-3">
                                        {note.created_by === 'IA' || (note.text || '').startsWith('[Resumo IA]') ? (
                                          <div className="ai-summary-markdown">
                                            <Badge variant="secondary" className="bg-blue-600 text-white mb-3 text-xs">
                                              Resumo IA
                                            </Badge>
                                            <ReactMarkdown
                                              remarkPlugins={[remarkGfm]}
                                              components={{
                                                h2: ({ node, ...props }) => (
                                                  <h2 className="text-base font-bold text-primary mb-2 mt-4 first:mt-0 border-b border-blue-200 dark:border-blue-800 pb-1" {...props} />
                                                ),
                                                h3: ({ node, ...props }) => (
                                                  <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2 mt-3" {...props} />
                                                ),
                                                ul: ({ node, ...props }) => (
                                                  <ul className="space-y-1 mb-3 ml-2" {...props} />
                                                ),
                                                li: ({ node, ...props }) => (
                                                  <li className="flex items-start gap-1.5 text-sm text-slate-600 dark:text-slate-400" {...props}>
                                                    <span className="text-blue-400 mt-1">•</span>
                                                    <span className="flex-1">{props.children}</span>
                                                  </li>
                                                ),
                                                strong: ({ node, ...props }) => (
                                                  <strong className="font-semibold text-slate-900 dark:text-slate-100" {...props} />
                                                ),
                                                p: ({ node, ...props }) => (
                                                  <p className="text-sm text-slate-700 dark:text-slate-300 mb-2 leading-relaxed" {...props} />
                                                ),
                                                hr: ({ node, ...props }) => (
                                                  <hr className="my-4 border-t border-blue-200 dark:border-blue-800" {...props} />
                                                ),
                                              }}
                                            >
                                              {(note.text || note.content || '').replace(/^\[Resumo IA\]\s*/, '')}
                                            </ReactMarkdown>
                                          </div>
                                        ) : (
                                          <p className="text-sm whitespace-pre-wrap text-slate-700">
                                            {note.text || note.content}
                                          </p>
                                        )}
                                        <p className="text-xs text-slate-500 mt-2">
                                          {note.created_date && safeFormat(new Date(note.created_date), 'PPp')}
                                          {note.created_by && ` • ${note.created_by}`}
                                        </p>
                                      </CardContent>
                                    </Card>
                                  ))}
                                </div>
                              ) : (
                                <p className="text-sm text-slate-400 italic">
                                  {t('calls.noNotesAddedYet')}
                                </p>
                              );
                            })()}
                          </div>
                          
                          {call.recordings && call.recordings.length > 0 && (
                            <div className="pt-3 border-t border-slate-200">
                              <p className="text-xs font-medium text-slate-600 mb-2">{t('calls.recordings')}:</p>
                              <div className="space-y-3">
                                {call.recordings.map((recording) => {
                                  const isThisPlaying = playingRecordingId === recording.id;
                                  return (
                                    <div key={recording.id} className="space-y-2">
                                      <div className="flex items-center gap-2">
                                        <Button
                                          size="sm"
                                          variant={isThisPlaying && isPlaying ? "default" : "outline"}
                                          onClick={() => handlePlayRecording(recording)}
                                          className={`flex items-center gap-2 ${isThisPlaying && isPlaying ? 'animate-pulse' : ''}`}
                                        >
                                          {isThisPlaying ? (
                                            isPlaying ? (
                                              <Pause className="w-3 h-3" />
                                            ) : (
                                              <Play className="w-3 h-3" />
                                            )
                                          ) : (
                                            <Play className="w-3 h-3" />
                                          )}
                                          {recording.duration_seconds 
                                            ? `${formatDuration(recording.duration_seconds)}` 
                                            : 'Reproduzir'}
                                        </Button>
                                      </div>
                                      {isThisPlaying && duration > 0 && (
                                        <div className="flex items-center gap-3 px-2">
                                          <span className="text-xs text-slate-600 min-w-[40px]">
                                            {formatDuration(currentTime)}
                                          </span>
                                          <Slider
                                            value={[currentTime]}
                                            max={duration}
                                            step={0.1}
                                            onValueChange={handleSeek}
                                            className="flex-1"
                                          />
                                          <span className="text-xs text-slate-600 min-w-[40px]">
                                            {formatDuration(duration)}
                                          </span>
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                          
                          {/* Video Recording Link */}
                          {call.channel === 'video' && call.metadata?.daily_recording_url && (
                            <div className="pt-3 border-t border-slate-200">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  const newTab = window.open('about:blank', '_blank');
                                  try {
                                    // Try existing URL first with a HEAD check
                                    let url = call.metadata.daily_recording_url;
                                    try {
                                      const head = await fetch(url, { method: 'HEAD' });
                                      if (head.ok) {
                                        newTab.location.href = url;
                                        return;
                                      }
                                    } catch {}
                                    // URL expired — refresh via edge function
                                    const payload = {};
                                    if (call.metadata.daily_recording_id) payload.recording_id = call.metadata.daily_recording_id;
                                    else if (call.metadata.daily_room_name) payload.room_name = call.metadata.daily_room_name;
                                    else { newTab.close(); toast.error('Sem dados para renovar o link da gravação'); return; }
                                    const { data, error } = await supabase.functions.invoke('get-daily-recording-access-link', { body: payload });
                                    if (error || !data?.download_link) {
                                      newTab.close();
                                      toast.error('Não foi possível obter o link da gravação');
                                      return;
                                    }
                                    newTab.location.href = data.download_link;
                                  } catch (err) {
                                    newTab?.close();
                                    toast.error('Erro ao abrir gravação');
                                  }
                                }}
                                className="flex items-center gap-2"
                              >
                                <Play className="w-3 h-3" />
                                Ver Gravação
                              </Button>
                            </div>
                          )}

                          {/* Video Ring Details Panel - shows escalation history */}
                          {call.channel === 'video' && (
                            <div className="pt-3 border-t border-slate-200">
                              <VideoRingDetailsPanel callId={call.id} />
                            </div>
                          )}
                        </CollapsibleContent>
                      </div>
                    </div>
                  </CardContent>
                </Collapsible>
              </Card>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-16 text-slate-400">
          <Phone className="w-16 h-16 mx-auto mb-4" />
          <p>No calls recorded</p>
          <p className="text-sm mt-2">Calls will appear here automatically</p>
        </div>
      )}
    </TabsContent>

    <TabsContent value="scheduled">
      <div className="space-y-4">
        {scheduledLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
          </div>
        ) : scheduledCalls.length === 0 ? (
          <p className="text-slate-500 text-center py-8">No scheduled calls</p>
        ) : (
          <div className="grid grid-cols-1 gap-4">
            {scheduledCalls.map((call) => (
              <ScheduledCallCard
                key={call.id}
                call={call}
                contactName={call.handler?.display_name}
                onEdit={() => handleEditScheduledCall(call)}
                onReschedule={() => {
                  setCallToReschedule(call);
                  setRescheduleModalOpen(true);
                }}
                onComplete={async () => {
                  // Block premature completion for scheduled calls
                  if (call.scheduled_for) {
                    const scheduledDate = new Date(call.scheduled_for);
                    const now = new Date();
                    if (now < scheduledDate) {
                      toast.error(`Esta chamada só pode ser completada a partir de ${formatDateTimeEN(call.scheduled_for)}`);
                      return;
                    }
                  }
                  
                  // Check if it's a referral_request call
                  if (call.call_purpose === 'referral_request') {
                    setSelectedCallForReferral(call);
                    setReferralModalOpen(true);
                    return;
                  }
                  
                  try {
                    await markCallComplete(call.id);
                    // Force reload both lists to ensure completed call appears
                    await loadCalls();
                    await loadScheduledCalls();
                    toast.success("Reunião completada com sucesso!");
                  } catch (error) {
                    toast.error("Erro ao completar reunião");
                  }
                }}
                onCancel={() => handleOpenCancelConfirm(call)}
                onDelete={async () => {
                  if (confirm("Are you sure you want to delete this scheduled call?")) {
                    try {
                      await deleteScheduledCall(call.id);
                      toast.success("Call deleted");
                    } catch (error) {
                      toast.error("Error deleting call");
                    }
                  }
                }}
              />
            ))}
          </div>
        )}
      </div>
    </TabsContent>
  </Tabs>

      <ScheduleCallModal
        open={showScheduleModal}
        onClose={handleCloseScheduleModal}
        onSchedule={handleScheduleCall}
        contactId={contactId}
        leadId={leadId}
        dealId={dealId}
        initialData={editingCall}
        isEditing={!!editingCall}
      />
      
      {/* Referral Completion Modal */}
      {selectedCallForReferral && (
        <ReferralCallCompletionModal
          open={referralModalOpen}
          onClose={() => {
            setReferralModalOpen(false);
            setSelectedCallForReferral(null);
          }}
          call={selectedCallForReferral}
        />
      )}
      
      {/* Reschedule Call Modal */}
      {callToReschedule && (
        <RescheduleCallModal
          open={rescheduleModalOpen}
          onClose={() => {
            setRescheduleModalOpen(false);
            setCallToReschedule(null);
          }}
          call={callToReschedule}
          onReschedule={rescheduleCall}
        />
      )}

      {/* Cancel Confirmation Modal */}
      <AlertDialog open={cancelConfirmOpen} onOpenChange={setCancelConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <XCircle className="w-5 h-5 text-orange-500" />
              Cancelar Reunião
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-4">
                <p>
                  Você tem certeza que deseja cancelar esta reunião?
                  {callToCancel?.title && (
                    <span className="font-medium block mt-2">
                      "{callToCancel.title}"
                    </span>
                  )}
                </p>
                <div className="space-y-2">
                  <Label htmlFor="cancel-reason">Motivo do cancelamento (opcional)</Label>
                  <Textarea
                    id="cancel-reason"
                    value={cancelReason}
                    onChange={(e) => setCancelReason(e.target.value)}
                    placeholder="Informe o motivo do cancelamento..."
                    rows={2}
                  />
                </div>
                <p className="text-sm text-muted-foreground">
                  Esta ação irá cancelar a reunião e remover do Google Calendar.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Voltar</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleConfirmCancel}
              className="bg-orange-600 hover:bg-orange-700"
            >
              Confirmar Cancelamento
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
</div>
);
}